import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { ChatCircleText, FileText, Users, WarningCircle, CheckCircle, XCircle, ClockCounterClockwise, PaperPlaneTilt } from "@phosphor-icons/react";

const stat = (label, value, Icon, color, tid) => (
  <Card data-testid={tid} className="p-5 border border-slate-200 shadow-none rounded-md">
    <div className="flex items-center justify-between">
      <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500">{label}</div>
      <Icon size={18} weight="regular" className={color} />
    </div>
    <div className="mt-3 font-[Manrope] font-extrabold text-3xl tracking-tight text-slate-900 tabular-nums">{value}</div>
  </Card>
);

export default function Dashboard() {
  const [s, setS] = useState(null);
  const [recent, setRecent] = useState([]);
  const [acts, setActs] = useState([]);

  useEffect(() => {
    (async () => {
      const [a, m, act] = await Promise.all([
        api.get("/stats"), api.get("/messages?limit=6"), api.get("/activities?limit=6"),
      ]);
      setS(a.data); setRecent(m.data); setActs(act.data);
    })();
  }, []);

  if (!s) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Overview</div>
        <h1 className="mt-1 font-[Manrope] font-extrabold text-4xl sm:text-5xl tracking-tight text-slate-900">
          Command deck.
        </h1>
        <p className="mt-2 text-slate-600 max-w-2xl leading-relaxed">
          A single pane to triage client conversations, drive documents to completion, and dispatch the daily flash report.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stat("Unread messages", s.unread_messages, ChatCircleText, "text-blue-600", "stat-unread")}
        {stat("Urgent", s.urgent_messages, WarningCircle, "text-red-600", "stat-urgent")}
        {stat("Clients", s.clients, Users, "text-slate-700", "stat-clients")}
        {stat("Contacts", s.contacts, Users, "text-slate-700", "stat-contacts")}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {stat("Docs · Total", s.documents.total, FileText, "text-slate-700", "stat-docs-total")}
        {stat("Pending", s.documents.Pending, ClockCounterClockwise, "text-amber-600", "stat-docs-pending")}
        {stat("Approved", s.documents.Approved, CheckCircle, "text-emerald-600", "stat-docs-approved")}
        {stat("Rejected", s.documents.Rejected, XCircle, "text-red-600", "stat-docs-rejected")}
        {stat("Forwarded DEXT", s.documents["Forwarded to DEXT"], PaperPlaneTilt, "text-blue-600", "stat-docs-forwarded")}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6 border border-slate-200 shadow-none rounded-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-[Manrope] font-bold text-lg tracking-tight">Recent messages</h3>
            <a href="/inbox" className="text-xs text-slate-500 hover:text-slate-900 font-medium">Open inbox →</a>
          </div>
          <div className="divide-y divide-slate-100">
            {recent.length === 0 && <div className="py-6 text-sm text-slate-500">No messages yet. Simulate one in the Inbox.</div>}
            {recent.map((m) => (
              <div key={m.id} className="py-3 flex items-start gap-3">
                <div className={`mt-1 h-2 w-2 rounded-full ${m.direction === "inbound" ? "bg-blue-500" : "bg-slate-300"}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs font-mono text-slate-500">
                    <span>{m.contact_phone}</span>
                    <span>·</span>
                    <span>{new Date(m.created_at).toLocaleString()}</span>
                    {m.urgency === "high" && <span className="px-1.5 py-0.5 rounded border border-red-200 bg-red-50 text-red-700 text-[10px]">URGENT</span>}
                  </div>
                  <div className="text-sm text-slate-800 truncate">{m.body}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6 border border-slate-200 shadow-none rounded-md">
          <h3 className="font-[Manrope] font-bold text-lg tracking-tight mb-4">Latest activity</h3>
          <div className="space-y-3">
            {acts.length === 0 && <div className="text-sm text-slate-500">No activity yet.</div>}
            {acts.map(a => (
              <div key={a.id} className="text-sm">
                <div className="font-medium text-slate-800">{a.action}</div>
                <div className="text-xs text-slate-500 font-mono">{a.actor} · {new Date(a.created_at).toLocaleTimeString()}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
